import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  IterableChanges,
  IterableDiffer,
  IterableDiffers,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { Subject, fromEvent, interval } from 'rxjs';
import { sample, takeUntil } from 'rxjs/operators';
import {
  AnnouncementItemViewModel,
  AttendanceItemViewModel,
  ChannelArticleItemViewModel,
  ItemViewModel,
  PaymentItemViewModel,
  UnknownItemViewModel
} from 'src/app/models/item.model';
import { VirtualScrollerComponent } from 'src/external/ngx-virtual-scroller/projects/ngx-virtual-scroller/src/public-api';
import { VirtualScrollerComponent as VirtualScrollerComponent_1 } from '../../../external/ngx-virtual-scroller/projects/ngx-virtual-scroller/src/lib/ngx-virtual-scroller.component';
import {
  SelectAttendanceAlertsEventPayload,
  SelectStudentEventPayload,
  ShowPaymentEventPayload,
  ShowSlipEventPayload
} from '../../models/event-payloads.model';
import { HubItemComponent } from '../hub-item/hub-item.component';

@Component({
  selector: 'app-hub-item-list',
  templateUrl: './hub-item-list.component.html',
  styleUrls: ['./hub-item-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [VirtualScrollerComponent_1, HubItemComponent, IonSpinner, IonIcon]
})
export class HubItemListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() items: (
    | ChannelArticleItemViewModel |
    AnnouncementItemViewModel |
    AttendanceItemViewModel |
    PaymentItemViewModel |
    UnknownItemViewModel
  )[];
  @Input() disablePublisherLink: boolean;
  @Input() parentLoading: boolean;
  @Input() showSearchMore: boolean;
  @Input() lastItemReached: boolean;
  @Output() selectChannel = new EventEmitter<number>();
  @Output() selectStudent = new EventEmitter<SelectStudentEventPayload>();
  @Output() selectItem = new EventEmitter<string>();
  @Output() selectAttendanceAlerts = new EventEmitter<SelectAttendanceAlertsEventPayload>();
  @Output() selectPaymentRequests = new EventEmitter<number>();
  @Output() toggleItemDone = new EventEmitter<number>();
  @Output() shareItem = new EventEmitter<number>();
  @Output() translateItem = new EventEmitter<number>();
  @Output() showOriginalItem = new EventEmitter<number>();
  @Output() itemRead = new EventEmitter<number>();
  @Output() openAttachment = new EventEmitter<string>();
  @Output() getThumb = new EventEmitter<string>();
  @Output() atEnd = new EventEmitter<boolean>();
  @Output() serverSearch = new EventEmitter();
  @Output() loadMore = new EventEmitter();
  @Output() showPayment = new EventEmitter<ShowPaymentEventPayload>();
  @Output() showSlip = new EventEmitter<ShowSlipEventPayload>();
  @Output() reactWith = new EventEmitter<{ itemSequenceNumber: number; reactionId: string }>();

  @ViewChild(VirtualScrollerComponent)
  private vsComponent: VirtualScrollerComponent;
  // Each time the list is rebound (i.e. any change to state) the items get recreated so
  // keep track of item ids to prevent superfluous event emission
  private itemsSeen = [];
  el: any;
  virtualItems: { sequenceNumber: number; item: ItemViewModel }[] = [];
  private differ?: IterableDiffer<any>;
  private scrollPosition = 0;
  destroyed$: Subject<boolean>;

  constructor(
    private cd: ChangeDetectorRef,
    private element: ElementRef,
    private iterableDiffers: IterableDiffers
  ) {}
  ngOnInit(): void {
    this.destroyed$ = new Subject<boolean>();

    const ionContent = this.element.nativeElement.closest('ion-content');
    fromEvent(ionContent, 'ionScroll')
      .pipe(takeUntil(this.destroyed$), sample(interval(200)))
      .subscribe((_) => {
        const newViewPortInfo = this.vsComponent.viewPortInfo;
        // using overscroll to compensate for Android where items do not fill screen
        // we get the scroll event but start position is zero each time
        const scrollingDown =
          newViewPortInfo.scrollStartPosition === 0 || this.scrollPosition < newViewPortInfo.scrollStartPosition;
        this.scrollPosition = newViewPortInfo.scrollStartPosition;
        if (scrollingDown && newViewPortInfo.endIndex >= this.virtualItems.length - 3) {
          // console.log('loading more fired', this.scrollPosition, newViewPortInfo, this.virtualItems);
          this.loadMore.emit();
        }
      });
    ionContent.getScrollElement().then((el) => {
      this.el = el;
      this.cd.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.destroyed$) {
      this.destroyed$.next(true);
      this.destroyed$.complete();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('items' in changes) {
      const value = changes.items.currentValue
        ? changes.items.currentValue.map(i => ({ sequenceNumber: i.sequenceNumber, item: i }))
        : [];
      if (this.differ === undefined) {
        this.differ = this.iterableDiffers.find(value).create(this.trackByFn);
      }
      this.applyChanges();
    }
  }

  // ngx-virtual-scroller doesn't cope well with changing the 'items' input, such that
  // the reference to an already existing item changes, so we have
  // to track changes manually and mutate the original array ensuring we preserve existing
  // item references
  applyChanges(): void {
    const changes: IterableChanges<{ sequenceNumber: number; item: ItemViewModel }> =
      this.differ !== undefined && this.items
        ? this.differ.diff(this.items.map(i => ({ sequenceNumber: i.sequenceNumber, item: i })))
        : null;

    if (changes) {
      changes.forEachRemovedItem((record) => {
        // console.log('removed', record);
        this.virtualItems = this.virtualItems.filter(i => i.sequenceNumber !== record.item.sequenceNumber);
      });
    }

    this.virtualItems.forEach((myItem) => {
      const updatedItem = this.items.find(item => item.sequenceNumber === myItem.sequenceNumber);
      if (updatedItem && this.itemDiffer(myItem.item, updatedItem) !== 0) {
        // console.log('updated', updatedItem);
        myItem.item = updatedItem;
      }
    });

    if (changes) {
      const additions = [];
      changes.forEachAddedItem((record) => {
        // console.log('added', record);
        additions.push({ sequenceNumber: record.item.sequenceNumber, item: record.item.item });
      });
      if (additions.length > 0) {
        this.virtualItems.push(...additions);
        this.virtualItems = this.virtualItems.sort((a, b) => b.sequenceNumber - a.sequenceNumber);
      }
    }
  }

  itemDiffer(a: ItemViewModel, b: ItemViewModel): number {
    return (
      this.compare(a.done, b.done) ||
      this.compare(a.read, b.read) ||
      this.compare(a.title, b.title) ||
      this.compare(a.message, b.message) ||
      this.compare(JSON.stringify(a.translation), JSON.stringify(b.translation)) ||
      this.compare(JSON.stringify(a.reaction), JSON.stringify(b.reaction)) ||
      this.compare(JSON.stringify(a.createdBy), JSON.stringify(b.createdBy)) ||
      this.compare(JSON.stringify(a.attachments), JSON.stringify(b.attachments))
    );
  }

  // https://codereview.stackexchange.com/a/177259
  private compare<T>(a: T, b: T): number {
    if (a === b) return 0;
    if (a === null) return -1;
    if (b === null) return 1;

    // To appease the type system, check both
    // I think it might be a bug that this is required
    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b, 'standard', { sensitivity: 'case' });
    }

    return b > a ? -1 : 1;
  }

  itemInView(item: ItemViewModel): void {
    if (!this.itemsSeen.includes(item.sequenceNumber)) {
      this.itemsSeen.push(item.sequenceNumber);
      if (!item.read) {
        this.itemRead.emit(item.sequenceNumber);
      }
    }
  }

  trackByFn(index: number, item: ItemViewModel): number {
    return item.sequenceNumber;
  }

  emitServerSearch(): void {
    this.serverSearch.emit();
  }
}
